import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Plus, Tag, Pause, Play, Loader2 } from "lucide-react";

const API = "/api";
const PCTS = [10, 15, 20, 25, 30, 40, 50];

const EMPTY = { code: "", type: "percent", percent: 20, fixed_amounts_text: "", countries_text: "ALL", plans: [6, 12], expires_at: "", max_uses: 100, max_per_user: 1, partner_name: "" };

// CORTE 5-A — CRUD mínimo de cupones (el panel completo llega en 5-C). Admin en español.
export default function AdminCoupons() {
  const [coupons, setCoupons] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [calc, setCalc] = useState(null);
  const [calcPct, setCalcPct] = useState(10);
  const [calcLoading, setCalcLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/coupons`);
      setCoupons(r.data.coupons || []);
    } catch (_) { setCoupons([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const buildBody = () => {
    const body = {
      code: form.code, type: form.type,
      percent: form.type === "percent" ? Number(form.percent) : null,
      countries: form.countries_text.trim().toUpperCase() === "ALL" ? ["ALL"]
        : form.countries_text.split(",").map((c) => c.trim().toUpperCase()).filter(Boolean),
      plans: form.plans, expires_at: form.expires_at || null,
      max_uses: Number(form.max_uses), max_per_user: Number(form.max_per_user),
      partner_name: form.partner_name || null, status: "active",
    };
    if (form.type === "fixed") {
      const fx = {};
      form.fixed_amounts_text.split(",").forEach((pair) => {
        const [cur, amt] = pair.split(":").map((s) => s.trim());
        if (cur && amt) fx[cur.toUpperCase()] = Number(amt);
      });
      body.fixed_amounts = fx;
    }
    return body;
  };

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/coupons`, buildBody());
      toast.success("Cupón creado");
      setForm(EMPTY); setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail === "code_exists" ? "Ese código ya existe" : `Error: ${e.response?.data?.detail || "no se pudo crear"}`);
    } finally { setSaving(false); }
  };

  const toggleStatus = async (c) => {
    try {
      await axios.put(`${API}/admin/coupons/${c.id}`, {
        code: c.code, type: c.type, percent: c.percent, fixed_amounts: c.fixed_amounts,
        countries: c.countries, plans: c.plans, expires_at: c.expires_at,
        max_uses: c.max_uses, max_per_user: c.max_per_user, partner_name: c.partner_name,
        status: c.status === "active" ? "paused" : "active",
      });
      toast.success(c.status === "active" ? "Cupón pausado" : "Cupón activado");
      await load();
    } catch (_) { toast.error("No se pudo actualizar"); }
  };

  const inputCls = "w-full bg-[#14375F] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500";

  // 5-C — calculadora de neto EN VIVO: toda la matemática la hace el backend
  const runCalculator = async () => {
    setCalcLoading(true);
    try {
      const body = buildBody();
      const r = await axios.post(`${API}/admin/coupons/net-calculator`, {
        type: body.type, percent: body.percent, fixed_amounts: body.fixed_amounts,
        countries: body.countries, plans: body.plans, partner_pct: Number(calcPct) || 0,
      });
      setCalc(r.data.rows || []);
    } catch (_) { toast.error("No se pudo calcular"); }
    finally { setCalcLoading(false); }
  };

  const visibleCoupons = (coupons || []).filter((c) => {
    const q = search.trim().toUpperCase();
    if (!q) return true;
    return (c.code || "").includes(q) || (c.partner_name || "").toUpperCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#0B2545] p-4 sm:p-8" data-testid="admin-coupons-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm" data-testid="coupons-back-link">
          <ArrowLeft className="w-4 h-4" /> Volver al panel
        </Link>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Tag className="w-6 h-6 text-[#5BC0FF]" /> Cupones de descuento
          </h1>
          <Button onClick={() => setShowForm(!showForm)} className="bg-[#1B84FF] hover:bg-emerald-700 rounded-full" data-testid="coupon-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo cupón
          </Button>
        </div>

        {showForm && (
          <div className="bg-[#14375F]/60 border border-white/15 rounded-2xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coupon-form">
            <div>
              <label className="text-xs text-slate-400">Código</label>
              <input className={inputCls + " font-mono uppercase"} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="CHOQUECULTURAL" data-testid="coupon-form-code" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Tipo</label>
              <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="coupon-form-type">
                <option value="percent">Porcentaje</option>
                <option value="fixed">Monto fijo por moneda</option>
              </select>
            </div>
            {form.type === "percent" ? (
              <div>
                <label className="text-xs text-slate-400">Descuento %</label>
                <select className={inputCls} value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} data-testid="coupon-form-percent">
                  {PCTS.map((p) => <option key={p} value={p}>{p}%</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="text-xs text-slate-400">Montos (CUR:monto, …)</label>
                <input className={inputCls} value={form.fixed_amounts_text} onChange={(e) => setForm({ ...form, fixed_amounts_text: e.target.value })} placeholder="PEN:10, INR:40" data-testid="coupon-form-fixed" />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-400">Países (ALL o PE,IN,AR…)</label>
              <input className={inputCls + " uppercase"} value={form.countries_text} onChange={(e) => setForm({ ...form, countries_text: e.target.value })} data-testid="coupon-form-countries" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Planes</label>
              <div className="flex gap-2 mt-1">
                {[6, 12].map((m) => (
                  <button key={m} type="button"
                    onClick={() => setForm({ ...form, plans: form.plans.includes(m) ? form.plans.filter((x) => x !== m) : [...form.plans, m] })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${form.plans.includes(m) ? "border-emerald-400 bg-[#1B84FF]/20 text-emerald-300" : "border-slate-600 text-slate-400"}`}
                    data-testid={`coupon-form-plan-${m}`}>
                    {m} meses
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">Vence (opcional, ISO)</label>
              <input type="date" className={inputCls} value={form.expires_at?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value ? `${e.target.value}T23:59:59+00:00` : "" })} data-testid="coupon-form-expires" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Usos máx totales</label>
              <input type="number" min="1" className={inputCls} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="coupon-form-max-uses" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Usos máx por usuario</label>
              <input type="number" min="1" className={inputCls} value={form.max_per_user} onChange={(e) => setForm({ ...form, max_per_user: e.target.value })} data-testid="coupon-form-max-per-user" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Partner (nombre, opcional)</label>
              <input className={inputCls} value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} placeholder="Choquecultural" data-testid="coupon-form-partner" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <Button onClick={create} disabled={saving || !form.code.trim()} className="bg-[#1B84FF] hover:bg-emerald-700 rounded-full" data-testid="coupon-create-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Crear cupón
              </Button>
            </div>

            {/* 5-C — Calculadora de neto en vivo (matemática 100% backend) */}
            <div className="sm:col-span-2 lg:col-span-3 border-t border-white/15 pt-4" data-testid="net-calculator">
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <p className="text-sm font-semibold text-white">Calculadora de neto</p>
                <label className="text-xs text-slate-400">% comisión del partner
                  <input type="number" min="0" max="50" className={inputCls + " w-24 mt-1"} value={calcPct}
                    onChange={(e) => setCalcPct(e.target.value)} data-testid="calc-partner-pct" />
                </label>
                <Button onClick={runCalculator} disabled={calcLoading} className="bg-sky-600 hover:bg-sky-500 rounded-full h-9" data-testid="calc-run-btn">
                  {calcLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Calcular"}
                </Button>
                <span className="text-[10px] text-slate-400">Advertencia (no bloqueo) si el neto baja del 15% del precio original.</span>
              </div>
              {calc && (
                <div className="overflow-x-auto max-h-72 overflow-y-auto" data-testid="calc-results">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#14375F]">
                      <tr className="text-slate-400 text-left">
                        <th className="py-1.5 pr-2">País</th><th className="pr-2">Plan</th>
                        <th className="pr-2">Precio final al cliente</th><th className="pr-2">Cobro</th>
                        <th className="pr-2">Al partner</th><th className="pr-2">Neto AvisoCar</th><th>% neto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calc.map((r, i) => (
                        <tr key={i} className={`border-t border-white/15/50 ${r.warning_low_net ? "bg-amber-500/10" : ""}`} data-testid={`calc-row-${r.country}-${r.plan_months}`}>
                          <td className="py-1.5 pr-2 font-semibold text-white">{r.country}</td>
                          <td className="pr-2 text-slate-200">{r.plan_months}m</td>
                          <td className="pr-2 text-emerald-300">{r.display_final} {r.display_currency} <span className="text-slate-500 line-through">{r.display_original}</span></td>
                          <td className="pr-2 text-slate-200">{r.charge_final} {r.charge_currency}</td>
                          <td className="pr-2 text-purple-300">{r.partner_amount} {r.charge_currency}</td>
                          <td className="pr-2 text-white font-semibold">{r.net} {r.charge_currency}</td>
                          <td className={r.warning_low_net ? "text-amber-300 font-bold" : "text-slate-300"}>
                            {r.net_pct}% {r.warning_low_net && "⚠ bajo 15%"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        <input className={inputCls + " mb-4 max-w-xs"} placeholder="Buscar por código o partner…" value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="coupon-search" />

        {!coupons ? (
          <div className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
        ) : coupons.length === 0 ? (
          <p className="text-slate-500 italic" data-testid="coupons-empty">Sin cupones todavía.</p>
        ) : (
          <div className="space-y-3" data-testid="coupons-list">
            {visibleCoupons.map((c) => (
              <div key={c.id} className="bg-[#14375F]/60 border border-white/15 rounded-xl p-4 flex items-center gap-3" data-testid={`coupon-row-${c.code}`}>
                <div className="flex-1 flex flex-wrap items-center gap-3 min-w-0">
                <code className="font-mono font-bold text-emerald-300 text-lg">{c.code}</code>
                <Badge className="bg-sky-500/20 text-sky-300">
                  {c.type === "percent" ? `${c.percent}%` : Object.entries(c.fixed_amounts || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}
                </Badge>
                <span className="text-slate-400 text-xs">
                  {(c.countries || []).includes("ALL") ? "🌎 Todos los países" : (c.countries || []).join(", ")} · planes {(c.plans || []).join("/")}m
                </span>
                <span className="text-slate-300 text-sm font-semibold" data-testid={`coupon-uses-${c.code}`}>
                  {c.uses_confirmed || 0}/{c.max_uses} confirmados · {c.uses_reserved || 0} reservas abiertas
                </span>
                {c.partner_name && <Badge className="bg-purple-500/20 text-purple-300">{c.partner_name}</Badge>}
                {Object.entries(c.revenue_by_currency || {}).map(([cur, v]) => (
                  <span
                    key={cur}
                    className="text-[11px] text-amber-300"
                    title="Suma bruta de pagos ya APROBADOS en esta moneda. No descuenta comisiones de socios y no incluye comprobantes pendientes."
                    data-testid={`coupon-revenue-${c.code}-${cur}`}
                  >
                    cobrado bruto {v} {cur}
                  </span>
                ))}
                {c.expires_at && <span className="text-slate-500 text-xs">vence {String(c.expires_at).slice(0, 10)}</span>}
                <Badge className={c.status === "active" ? "bg-[#1B84FF]/20 text-emerald-300" : "bg-slate-500/20 text-slate-400"} data-testid={`coupon-status-${c.code}`}>
                  {c.status === "active" ? "activo" : "pausado"}
                </Badge>
                </div>
                <Button size="sm" variant="outline" onClick={() => toggleStatus(c)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 rounded-full shrink-0"
                  data-testid={`coupon-toggle-${c.code}`}>
                  {c.status === "active" ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pausar</> : <><Play className="w-3.5 h-3.5 mr-1" /> Activar</>}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
